import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CustomDialog from "./CustomDialog";
import { formatCurrency } from "utils/commonFunctions";
import UserDetailFieldCard from "../UserDetailFieldCard";
import { DateRange } from "mui-daterange-picker";
import { buildOrderListPath } from "modules/dashboard/navigation";
import { ORDER_STATUS } from "utils/orderStatus";
import { styles } from "../styles";

interface Props {
  dashboardData: any;
  dateRangeHandler: (range: DateRange) => void;
  dateApplyHandler: () => void;
  startDate: Date;
  endDate: Date;
  franchiseId?: string | null;
  franchiseName?: string | null;
}
const OrderInformation: React.FC<Props> = (props) => {
  const {
    dashboardData,
    dateApplyHandler,
    dateRangeHandler,
    endDate,
    startDate,
    franchiseId,
    franchiseName,
  } = props;
  const navigate = useNavigate();

  const displayCount = (value: any) =>
    value === null || value === undefined || value === "" ? "N/A" : String(value);

  const totalOrders =
    dashboardData?.totalOrders ??
    dashboardData?.ordersCount?.reduce(
      (accumulator: number, order: any) =>
        accumulator + parseInt(order?.orderCount),
      0
    );

  const totalRevenue = dashboardData?.totalRevenue;
  const revenueDisplay =
    typeof totalRevenue === "number"
      ? formatCurrency(totalRevenue)
      : totalRevenue || "N/A";

  const openFranchiseOrders = (orderStatus: ORDER_STATUS) => {
    if (!franchiseId) return;
    navigate(
      buildOrderListPath(`${orderStatus}`, {
        merchantId: franchiseId,
        merchantName: franchiseName || undefined,
      }),
    );
  };

  return (
    <Box sx={styles.section}>
      <Box sx={styles.sectionHead}>
        <Typography component="h2" sx={styles.sectionTitle}>
          Order information
        </Typography>
        <CustomDialog
          dateRangeHandler={dateRangeHandler}
          dateApplyHandler={dateApplyHandler}
          startDate={startDate}
          endDate={endDate}
        />
      </Box>

      <Box sx={styles.grid}>
        <UserDetailFieldCard
          icon={<ReceiptLongOutlinedIcon />}
          heading="Total orders"
          data={displayCount(totalOrders)}
        />
        <UserDetailFieldCard
          icon={<PaymentsOutlinedIcon />}
          heading="Total revenue"
          data={revenueDisplay}
        />
        <UserDetailFieldCard
          icon={<PersonOffOutlinedIcon />}
          heading="Cancelled by customer"
          data={displayCount(
            dashboardData?.ordersCancelledByCustomer ??
              dashboardData?.canceledByCustomer
          )}
        />
        <UserDetailFieldCard
          icon={<StorefrontOutlinedIcon />}
          heading="Cancelled by franchise"
          data={displayCount(
            dashboardData?.ordersCancelledByFranchise ??
              dashboardData?.canceledByMerchant
          )}
          onClick={
            franchiseId
              ? () =>
                  openFranchiseOrders(ORDER_STATUS.CANCELLED_BY_SERVICE_PARTNER)
              : undefined
          }
        />
        <UserDetailFieldCard
          icon={<ForumOutlinedIcon />}
          heading="Total complaints"
          data={displayCount(dashboardData?.totalComplaints)}
        />
        <UserDetailFieldCard
          icon={<ReportProblemOutlinedIcon />}
          heading="Unresolved complaints"
          data={displayCount(dashboardData?.unresolvedComplaints)}
        />
        <UserDetailFieldCard
          icon={<BlockOutlinedIcon />}
          heading="Rejected by franchise"
          data={displayCount(
            dashboardData?.ordersRejectedByFranchise ??
              dashboardData?.rejectedByMerchant
          )}
          onClick={
            franchiseId
              ? () => openFranchiseOrders(ORDER_STATUS.REJECTED_BY_MERCHANT)
              : undefined
          }
        />
      </Box>
    </Box>
  );
};

export default OrderInformation;
